import { Alert, Box, Button, Stack } from '@mui/material';
import { useMemo, useState } from 'react';

import { useAnalytics } from '~/analytics';
import type { CfiFinding, CfiReport } from '~/data/hooks/useCfi';
import { useCfiMutations } from '~/data/hooks/useCfi';
import { BUTTON_SX } from '~/theme';

import { CfiBetaNotice } from './CfiBetaNotice';
import { CfiDownloadButton } from './CfiDownloadButton';
import { CfiFindingRow } from './CfiFindingRow';
import { CfiReportNarrative } from './CfiReportNarrative';
import { CfiSummaryDashboard } from './CfiSummaryDashboard';
import { finalCounts } from './finalCounts';

type Props = {
  contractId: string;
  report: CfiReport;
  canReopen: boolean;
  /** See `cfiLayout` — laid out for the full page rather than the drawer. */
  wide?: boolean;
};

export const CfiReportViewer = ({ contractId, report, canReopen, wide = false }: Props) => {
  const { reopen } = useCfiMutations(contractId);
  const { track } = useAnalytics();
  const [isBusy, setIsBusy] = useState(false);

  const handleReopen = async () => {
    setIsBusy(true);
    try {
      await reopen(report.id);
      track('cfi_report_reopened', { report_id: report.id });
    } finally {
      setIsBusy(false);
    }
  };

  const isSuperseded = report.status === 'superseded';
  const isPublished = report.status === 'published';

  const counts = useMemo(() => finalCounts(report.findings), [report.findings]);

  // Hidden no-op so we satisfy the patch typing on the row component.
  const handlePatch = async (
    _finding: CfiFinding,
    _body: Partial<{
      consultantRating: never;
      consultantSummary: never;
      consultantRecommendedAction: never;
      agreedWithAgent: never;
      excludedFromReport: never;
    }>,
  ) => Promise.resolve();

  return (
    <Box sx={{ width: '100%' }}>
      <CfiBetaNotice />

      {isSuperseded ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          A newer ContractIQ report exists for this contract — this one is no longer current.
        </Alert>
      ) : null}
      {isPublished ? (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            canReopen ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleReopen}
                disabled={isBusy}
                sx={BUTTON_SX}
              >
                Reopen for edits
              </Button>
            ) : null
          }
        >
          {`Published on ${report.publishedAt?.split('T')[0] ?? '—'}${
            report.previousPublishedAt ? ' (re-published after edits)' : ''
          }.`}
        </Alert>
      ) : null}

      {isPublished ? (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <CfiDownloadButton reportId={report.id} label="View report" />
        </Stack>
      ) : null}

      <CfiSummaryDashboard counts={counts} />

      <CfiReportNarrative
        title="Overall assessment"
        value={report.finalScopeSummary}
        canEdit={false}
        onSave={() => Promise.resolve()}
      />

      {report.findings
        .filter((f) => !f.excludedFromReport)
        .map((finding) => (
          <CfiFindingRow
            key={finding.id}
            finding={finding}
            canEdit={false}
            // Row is read-only when canEdit=false; no PATCH paths execute.
            onPatch={handlePatch as never}
            wide={wide}
          />
        ))}
    </Box>
  );
};
